import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

const RESULTS = [
  "7% ER achieved", "8x ROAS achieved", "1Mn followers reached",
  "5% CTR unlocked", "80% VTR unlocked", "₹39.7Cr EMV delivered",
  "300K growth in 4 months", "200+ videos monthly", "500M+ impressions",
  "30x engagement rate", "100+ phone launches", "2%+ CTR on performance",
  "0 to 600K followers", "₹50Cr pipeline built", "4.2% avg ER across brands",
  "Zero Hour IP created", "150+ films delivered", "3 Cannes shortlists",
];

const STATS = [
  { value: 500, suffix: "M+", label: "Impressions Delivered" },
  { value: 150, suffix: "+", label: "Films Produced" },
  { value: 50, suffix: "Cr+", label: "Revenue Pipeline", prefix: "₹" },
  { value: 3, suffix: "", label: "Cannes Shortlists" },
];

const SERVICES = ["Strategy", "Production", "Influencer Marketing", "Talent Management", "Performance", "Content Studio"];

function useCountUp(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCard({ stat, animate }) {
  const count = useCountUp(stat.value, 1800, animate);
  return (
    <div className="text-center px-6 py-4 border-l border-white/5 first:border-l-0">
      <div className="font-heading text-3xl sm:text-4xl text-white mb-1">
        {stat.prefix || ""}{animate ? count : 0}{stat.suffix}
      </div>
      <div className="text-[var(--t3)] text-xs tracking-widest uppercase">{stat.label}</div>
    </div>
  );
}

export default function MarketingPage() {
  const navigate = useNavigate();
  const [particles, setParticles] = useState([]);
  const [idCounter, setIdCounter] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => { setTimeout(() => setLoaded(true), 200); }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (Math.random() > 0.1) return;
    const result = RESULTS[Math.floor(Math.random() * RESULTS.length)];
    const id = idCounter;
    setIdCounter((c) => c + 1);
    setParticles((prev) => [
      ...prev.slice(-15),
      { id, x: e.clientX + (Math.random() - 0.5) * 80, y: e.clientY + (Math.random() - 0.5) * 50, text: result, angle: (Math.random() - 0.5) * 25 },
    ]);
    setTimeout(() => setParticles((prev) => prev.filter((p) => p.id !== id)), 2500);
  }, [idCounter]);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [handleMouseMove]);

  return (
    <div className="min-h-screen bg-[var(--s1)] flex flex-col relative overflow-hidden cursor-crosshair select-none" data-testid="marketing-page">

      {/* Dot grid background */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }} />

      {/* Noise overlay */}
      <div className="noise-overlay" />

      {/* Ambient red aura */}
      <div className="absolute top-[38%] left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ width: "900px", height: "600px" }}>
        <div className="w-full h-full" style={{ background: "radial-gradient(ellipse, rgba(227,25,55,0.10) 0%, rgba(227,25,55,0.03) 45%, transparent 70%)", animation: "heroAura 5s ease-in-out infinite" }} />
      </div>

      {/* Floating particles */}
      {particles.map((p) => (
        <div key={p.id} className="fixed pointer-events-none z-50" style={{ left: p.x, top: p.y, transform: `rotate(${p.angle}deg)`, animation: "resultFloat 2.5s ease-out forwards" }}>
          <span className="px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap backdrop-blur-md" style={{ background: "rgba(227,25,55,0.12)", border: "1px solid rgba(227,25,55,0.25)", color: "#ff6b81", boxShadow: "0 0 20px rgba(227,25,55,0.15)" }}>
            {p.text}
          </span>
        </div>
      ))}

      {/* Top bar */}
      <div className={`relative z-10 flex items-center justify-between px-6 sm:px-10 pt-8 transition-all duration-700 ${loaded ? "opacity-100" : "opacity-0"}`}>
        <span className="text-xs tracking-[0.4em] uppercase font-medium">
          <span className="text-white">DENTSU </span><span className="text-[#E31937]" style={{ animation: "subtleGlow 3s ease-in-out infinite" }}>AMP</span>
        </span>
        <button
          onClick={() => navigate("/login")}
          className="text-[var(--t3)] text-xs tracking-widest uppercase hover:text-[#E31937] transition-colors duration-300"
        >
          Team Login →
        </button>
      </div>

      {/* Hero */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 relative z-10 pt-8 pb-4">

        {/* Eyebrow */}
        <div className={`mb-6 transition-all duration-700 delay-100 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="text-[#E31937] text-xs tracking-[0.35em] uppercase font-medium">
            Full-suite integrated agency and content studio
          </span>
        </div>

        {/* Headline */}
        <h1
          className={`font-heading text-[clamp(3.5rem,12vw,9rem)] text-white leading-[0.85] tracking-tight mb-6 text-center transition-all duration-1000 delay-200 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ textShadow: "0 0 80px rgba(227,25,55,0.18), 0 0 160px rgba(227,25,55,0.06)" }}
        >
          RESULTS<br />AS A SERVICE
        </h1>

        {/* Services list */}
        <div className={`flex items-center justify-center flex-wrap gap-x-3 gap-y-1 mb-10 max-w-2xl transition-all duration-700 delay-300 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {SERVICES.map((s, i) => (
            <span key={s} className="inline-flex items-center gap-3">
              <span className="text-white/25 text-xs tracking-widest uppercase">{s}</span>
              {i < SERVICES.length - 1 && <span className="text-white/10 text-xs">—</span>}
            </span>
          ))}
        </div>

      </main>

      {/* Scrolling ticker */}
      <div className={`relative z-10 border-t border-white/5 py-3 overflow-hidden transition-all duration-700 delay-700 ${loaded ? "opacity-100" : "opacity-0"}`}>
        <div style={{ display: "flex", animation: "ticker 30s linear infinite", whiteSpace: "nowrap" }}>
          {[...RESULTS, ...RESULTS].map((r, i) => (
            <span key={i} className="inline-flex items-center gap-3 text-xs text-white/20 tracking-widest uppercase mx-6">
              <span className="w-1 h-1 rounded-full bg-[var(--r)] opacity-60 inline-block" />
              {r}
            </span>
          ))}
        </div>
      </div>

      {/* Floating orbs */}
      <div className="absolute top-[20%] left-[15%] w-2 h-2 rounded-full pointer-events-none" style={{ background: "rgba(227,25,55,0.4)", animation: "orbFloat1 12s ease-in-out infinite", boxShadow: "0 0 20px rgba(227,25,55,0.4)" }} />
      <div className="absolute top-[60%] right-[18%] w-1.5 h-1.5 rounded-full pointer-events-none" style={{ background: "rgba(6,182,212,0.35)", animation: "orbFloat2 15s ease-in-out infinite", boxShadow: "0 0 15px rgba(6,182,212,0.35)" }} />
      <div className="absolute top-[35%] right-[8%] w-1 h-1 rounded-full pointer-events-none" style={{ background: "rgba(245,158,11,0.35)", animation: "orbFloat3 18s ease-in-out infinite", boxShadow: "0 0 10px rgba(245,158,11,0.25)" }} />
      <div className="absolute bottom-[25%] left-[8%] w-1 h-1 rounded-full pointer-events-none" style={{ background: "rgba(227,25,55,0.25)", animation: "orbFloat2 20s ease-in-out infinite 3s", boxShadow: "0 0 12px rgba(227,25,55,0.2)" }} />

      {/* Scan line */}
      <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden">
        <div style={{ position: "absolute", left: 0, right: 0, height: "1px", background: "linear-gradient(90deg, transparent, rgba(227,25,55,0.06), transparent)", animation: "scanLine 10s linear infinite" }} />
      </div>


      <style>{`
        @keyframes resultFloat {
          0% { opacity: 0; transform: translateY(0) scale(0.8); }
          12% { opacity: 1; transform: translateY(-10px) scale(1); }
          85% { opacity: 0.7; }
          100% { opacity: 0; transform: translateY(-90px) scale(0.9); }
        }
        @keyframes heroAura {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.06); }
        }
        @keyframes breatheAura {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes subtleGlow {
          0%, 100% { text-shadow: 0 0 10px rgba(227,25,55,0.3); }
          50% { text-shadow: 0 0 25px rgba(227,25,55,0.6), 0 0 50px rgba(227,25,55,0.2); }
        }
        @keyframes shimmerBtn {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes orbFloat1 {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(30px, -20px); }
          50% { transform: translate(-10px, -40px); }
          75% { transform: translate(20px, -10px); }
        }
        @keyframes orbFloat2 {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(-25px, 15px); }
          66% { transform: translate(15px, -25px); }
        }
        @keyframes orbFloat3 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-20px, 20px); }
        }
        @keyframes scanLine {
          0% { top: -2px; }
          100% { top: 100%; }
        }
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
}
